import { useEffect, useRef, useState } from 'react';

export const INTAKE_SECTION_IDS = [
  'supplied',
  'preConditions',
  'postConditions',
  'photos',
  'm360',
] as const;

export type IntakeSectionId = (typeof INTAKE_SECTION_IDS)[number];

function storageKey(userId: string): string {
  return `fixflow_ticket_intake_sections:${userId}`;
}

export function loadIntakeSectionPrefs(userId: string): Partial<Record<IntakeSectionId, boolean>> {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Partial<Record<IntakeSectionId, boolean>>;
  } catch {
    return {};
  }
}

export function saveIntakeSectionPref(userId: string, id: IntakeSectionId, open: boolean): void {
  const prefs = loadIntakeSectionPrefs(userId);
  prefs[id] = open;
  window.localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
}

export function useIntakeSectionOpen(
  sectionId: IntakeSectionId,
  userId: string | undefined,
  needsAttention: boolean,
): { open: boolean; toggle: () => void } {
  const uid = userId || 'anon';
  const [sessionOverride, setSessionOverride] = useState<boolean | null>(null);
  const prevAttention = useRef(needsAttention);

  useEffect(() => {
    if (needsAttention && !prevAttention.current) {
      setSessionOverride(null);
    }
    prevAttention.current = needsAttention;
  }, [needsAttention]);

  const persisted = loadIntakeSectionPrefs(uid)[sectionId] ?? false;
  const open = sessionOverride ?? (needsAttention || persisted);

  function toggle(): void {
    const next = !open;
    setSessionOverride(next);
    saveIntakeSectionPref(uid, sectionId, next);
  }

  return { open, toggle };
}
